import axios from "axios";
import { BASE_URL } from "../components/Axios/base_uri";
import { store2 } from "../redux/store2";

const cleanBaseUrl = () => {
  return (BASE_URL || "https://api.sedihisham.com").replace(/\/+$/, "");
};

export const getAuthToken = (passedUser = null) => {
  try {
    const checkTarget = (target) => {
      if (!target) return "";
      if (typeof target === "string" && (target.startsWith("ey") || target.length > 25)) {
        return target;
      }
      if (target.access_token) return target.access_token;
      if (target.token) return target.token;
      if (target.accessToken) return target.accessToken;
      if (target.jwt) return target.jwt;

      if (Array.isArray(target)) {
        for (const item of target) {
          const res = checkTarget(item);
          if (res) return res;
        }
      }

      if (typeof target === "object") {
        if (target.user) {
          const userRes = checkTarget(target.user);
          if (userRes) return userRes;
        }
        if (target.auth) {
          const authRes = checkTarget(target.auth);
          if (authRes) return authRes;
        }
        for (const key of Object.keys(target)) {
          if (key.toLowerCase().includes("token")) {
            const val = target[key];
            if (typeof val === "string" && val.length > 20) return val;
          }
        }
      }

      return "";
    };

    // 1. Check passed user object
    if (passedUser) {
      const fromPassed = checkTarget(passedUser);
      if (fromPassed) return fromPassed;
    }

    // 2. Check active Redux store state in memory
    try {
      const state = store2.getState?.();
      if (state?.auth) {
        const fromRedux = checkTarget(state.auth);
        if (fromRedux) return fromRedux;
      }
    } catch (e) {}

    // 3. Direct localStorage keys
    const directKeys = [
      "token",
      "access_token",
      "accessToken",
      "jwt",
      "SediHishamAdmindata",
      "adminToken",
      "user",
      "sedi_token",
    ];
    for (const key of directKeys) {
      const val = localStorage.getItem(key);
      if (val) {
        if (typeof val === "string" && (val.startsWith("ey") || val.length > 25)) {
          return val;
        }
        try {
          const parsed = JSON.parse(val);
          const fromParsed = checkTarget(parsed);
          if (fromParsed) return fromParsed;
        } catch (e) {}
      }
    }

    // 4. Persisted Redux in localStorage
    const persisted = localStorage.getItem("persist:root");
    if (persisted) {
      try {
        const parsed = JSON.parse(persisted);
        if (parsed.auth) {
          const auth = typeof parsed.auth === "string" ? JSON.parse(parsed.auth) : parsed.auth;
          const fromAuth = checkTarget(auth);
          if (fromAuth) return fromAuth;
        }
      } catch (e) {}
    }
  } catch (error) {
    console.error("Error retrieving auth token:", error);
  }
  return "";
};

const getHeaders = (userContext = null) => {
  const token = getAuthToken(userContext);
  const cleanToken = token ? String(token).replace(/^Bearer\s+/i, "").trim() : "";
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  if (cleanToken) {
    headers["Authorization"] = `Bearer ${cleanToken}`;
    headers["token"] = cleanToken;
    headers["x-access-token"] = cleanToken;
  }

  return headers;
};

export const EcommerceSettingsService = {
  // 1️⃣ GET endpoint (Open without token)
  getSettings: async () => {
    const baseUrl = cleanBaseUrl();
    const candidateEndpoints = [
      `${baseUrl}/settings/ecommerce`,
      `${baseUrl}/settings/website`,
      `${baseUrl}/settings/ecommerce-settings`,
    ];

    let lastError = null;
    for (const url of candidateEndpoints) {
      try {
        const response = await axios.get(url, {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          timeout: 10000,
        });
        if (response && response.data) {
          // Normalize if wrapped in { data: ... }
          return response.data.data ? response.data.data : response.data;
        }
      } catch (err) {
        lastError = err;
        // If 404, try next alternative
        if (err.response && err.response.status === 404) {
          continue;
        }
        // If server returned other error, still try next endpoint just in case
      }
    }

    throw lastError || new Error("Failed to fetch ecommerce settings");
  },

  // 2️⃣ Update endpoint (POST / PUT / PATCH with Bearer Token)
  updateSettings: async (settingsData, userContext = null) => {
    const baseUrl = cleanBaseUrl();
    const headers = getHeaders(userContext);

    // Clean payload of read-only fields
    const payload = {
      show_prices: Boolean(settingsData.show_prices),
      show_cart: Boolean(settingsData.show_cart),
      show_checkout: Boolean(settingsData.show_checkout),
      show_buy_now: Boolean(settingsData.show_buy_now),
      show_login: Boolean(settingsData.show_login),
      show_register: Boolean(settingsData.show_register),
      show_auth_pages: Boolean(settingsData.show_auth_pages),
      show_wishlist: Boolean(settingsData.show_wishlist),
      show_orders: Boolean(settingsData.show_orders),
      ecommerce_active: Boolean(settingsData.ecommerce_active),
    };

    const attempts = [
      { method: "post", url: `${baseUrl}/settings/ecommerce` },
      { method: "post", url: `${baseUrl}/settings/ecommerce/update` },
      { method: "put", url: `${baseUrl}/settings/ecommerce` },
      { method: "patch", url: `${baseUrl}/settings/ecommerce` },
      { method: "put", url: `${baseUrl}/settings/ecommerce/update` },
      { method: "patch", url: `${baseUrl}/settings/ecommerce/update` },
    ];

    let lastError = null;
    for (const attempt of attempts) {
      try {
        const response = await axios({
          method: attempt.method,
          url: attempt.url,
          data: payload,
          headers,
          timeout: 10000,
        });
        if (response && response.data) {
          return response.data.data ? response.data.data : response.data;
        }
      } catch (err) {
        lastError = err;
        const status = err?.response?.status;
        // If 404 or 405, try other method/URL
        if (status === 404 || status === 405) {
          continue;
        }
        // If 401 unauthorized or 403, might be token issue or method issue, try POST update too
        if (status === 401 || status === 403) {
          continue;
        }
      }
    }

    throw lastError || new Error("Failed to update ecommerce settings");
  },
};

export default EcommerceSettingsService;
